import type { ICreditDecisionLender, TitlingLender } from '@makemydeal/dr-platform-types';
import type { LenderApiAddress, LenderAddressResponse } from '@makemydeal/dr-dash-types';
import { LENDER_ADDRESS_MAX_LENGTH } from '@makemydeal/dr-shared-ui-utils/validationConfigs/lenderValidationConfig';

/**
 * Normalize the API zip for storage by stripping any separators (hyphens, spaces, etc.) and keeping
 * only alphanumerics — e.g. "11042-1123" -> "110421123", "K1A 0B1" -> "K1A0B1". This keeps the stored
 * value clean and within the field's max length regardless of how the API formats it, instead of only
 * handling the exact US ZIP+4 shape (which would let any other formatting slip through and risk a save
 * failure).
 */
const normalizeLenderZip = (zip?: string | null): string | null => {
    if (!zip) return zip ?? null;
    return zip.replace(/[^0-9A-Za-z]+/g, '');
};

const clampLength = (value: string | null | undefined, max: number): string | null => (value == null ? null : value.slice(0, max));

const clampState = (state: string | null | undefined): string | null =>
    state && state.length >= LENDER_ADDRESS_MAX_LENGTH.state ? state.slice(0, LENDER_ADDRESS_MAX_LENGTH.state) : null;

/**
 * Map the BFF address shape onto the redux LenderAddress shape used by the lender-address fields.
 * - `clampToApiSchema`: coerce each field to the persistence API's limits (max lengths + two-char state).
 *   Used for the credit-decision address, which has no editable UI field — an over-length/malformed value
 *   would otherwise fail on save with no way for the user to correct it.
 */
export const mapApiAddress = (address: LenderApiAddress, { clampToApiSchema = false } = {}): TitlingLender => {
    const mapped: TitlingLender = {
        line1: address.addressLine1 ?? null,
        line2: address.addressLine2 ?? null,
        city: address.city ?? null,
        state: address.state ?? null,
        postalCode: normalizeLenderZip(address.zip)
    };
    if (!clampToApiSchema) return mapped;

    return {
        line1: clampLength(mapped.line1, LENDER_ADDRESS_MAX_LENGTH.line1),
        line2: clampLength(mapped.line2, LENDER_ADDRESS_MAX_LENGTH.line2),
        city: clampLength(mapped.city, LENDER_ADDRESS_MAX_LENGTH.city),
        state: clampState(mapped.state),
        postalCode: clampLength(mapped.postalCode, LENDER_ADDRESS_MAX_LENGTH.postalCode)
    };
};

/** Normalize a (possibly partial/absent) address into the cacheable shape, defaulting missing fields to null. */
export const toLenderData = (source?: TitlingLender): TitlingLender => ({
    line1: source?.line1 ?? null,
    line2: source?.line2 ?? null,
    city: source?.city ?? null,
    state: source?.state ?? null,
    postalCode: source?.postalCode ?? null,
    phone: source?.phone ?? null,
    name: source?.name
});

/**
 * Resolve the titling lender address from a BFF response for the given offer type, walking a fallback chain
 * and picking the first source that carries a usable address (at least one meaningful address field):
 *   - lease:   lease-titling → titling → contracting
 *   - finance: titling → contracting
 * The lender name follows the selected source. Returns a fully-normalized address even when the response
 * carries none, so applying it clears any previously-selected lender's address.
 */
export const deriveTitlingLender = (response: LenderAddressResponse, isLease: boolean): TitlingLender => {
    const { titling, contracting } = response;
    const leaseTitling = { address: titling?.titlingLeaseLenderAddress, name: titling?.titlingLeaseLenderName };
    const retailTitling = { address: titling?.titlingLenderAddress, name: titling?.titlingLenderName };
    const contractTitling = { address: contracting?.contractingLenderAddress, name: contracting?.contractingLenderName };
    const candidates = isLease ? [leaseTitling, retailTitling, contractTitling] : [retailTitling, contractTitling];

    const selected = candidates.find(
        ({ address }) =>
            !!address &&
            [address.addressLine1, address.addressLine2, address.city, address.state, address.zip].some((field) => !!field?.trim())
    );
    const mappedAddress = selected?.address ? mapApiAddress(selected.address) : toLenderData();
    return { ...mappedAddress, name: selected?.name };
};

/**
 * Resolve the credit-decision lender address from a BFF response.
 * - Prefer the contracting address when it has a valid zip, else fall back to original.
 * - Clamp to the API's field limits (`clampToApiSchema`): the address has no editable UI field, so a bad
 *   value would otherwise fail on save with no way to correct it.
 * - Carry phone/name from the lender-list entry, and embed its id (the cache key) so the result applies
 *   without a transform.
 * - Return the lender-list entry unchanged when neither address is present.
 */
export const deriveCreditDecisionLenderAddress = (
    response: LenderAddressResponse,
    lenderListLender: ICreditDecisionLender
): ICreditDecisionLender => {
    const contractingAddress = response.contracting?.contractingLenderAddress;
    const creditAddress =
        contractingAddress?.zip && contractingAddress.zip.trim() ? contractingAddress : response.original?.originalLenderAddress;
    if (!creditAddress) return lenderListLender;

    return {
        ...mapApiAddress(creditAddress, { clampToApiSchema: true }),
        ...(lenderListLender.phone ? { phone: lenderListLender.phone } : {}),
        name: lenderListLender.name,
        id: lenderListLender.id
    };
};
